import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useScripts } from '../hooks/useScripts';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import Footer from '../components/Footer';
import DownloadProfileModal from '../components/DownloadProfileModal';
import { VPN_SIGNUP_URL, VPN_EMAIL_CREDS_URL } from '../config';
import { setClarityTag } from '../utils/clarity';
import { resolveVpnProfileDownloadUrl } from '../utils/vpnDownloadLinks';

function extractKeyZipUrl(downloadLinksData) {
  if (!downloadLinksData || typeof downloadLinksData !== 'object') return null;
  if (downloadLinksData.key_zip_url) return downloadLinksData.key_zip_url;
  if (Array.isArray(downloadLinksData) && downloadLinksData.length > 0 && downloadLinksData[0].key_zip_url) {
    return downloadLinksData[0].key_zip_url;
  }
  for (const key of Object.keys(downloadLinksData)) {
    if (key === 'key_zip_url') return downloadLinksData[key];
    const val = downloadLinksData[key];
    if (val && typeof val === 'object' && val.key_zip_url) return val.key_zip_url;
  }
  return null;
}

/** True when the API error is about VPN user provisioning (avoid exposing backend product names). */
function isVpnServerUserCreationError(message) {
  if (!message || typeof message !== 'string') return false;
  const m = message.toLowerCase();
  return (
    m.includes('vpn account could not be created')
    || m.includes('pritunl')
    || m.includes('failed to create user in pritunl')
    || m.includes('failed to create user in vpn')
  );
}

function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'done' | 'error'
  const [errorPrimaryText, setErrorPrimaryText] = useState('');
  const [errorSecondaryText, setErrorSecondaryText] = useState('');
  const [downloadUrl, setDownloadUrl] = useState(null);
  const signupSent = useRef(false);

  useDocumentTitle(status === 'error' ? 'Setup Issue' : 'Payment Successful');
  useScripts();

  useEffect(() => {
    document.body.classList.add('login');
    return () => document.body.classList.remove('login');
  }, []);

  useEffect(() => {
    setClarityTag('purchases', true);
  }, []);

  useEffect(() => {
    if (!sessionId || signupSent.current) return;
    signupSent.current = true;
    setStatus('loading');
    const signupUrl = VPN_SIGNUP_URL.includes('?')
      ? VPN_SIGNUP_URL.replace('?', '.json?')
      : `${VPN_SIGNUP_URL.replace(/\/?$/, '')}.json`;
    fetch(signupUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        return { ok: r.ok, status: r.status, data };
      })
      .then(({ ok, status, data }) => {
        const body = data.body ?? data;
        const statusCode = data.statusCode ?? (ok ? 200 : status);
        const isSuccess = ok && statusCode >= 200 && statusCode < 300;
        if (isSuccess) {
          setStatus('done');
          const email = typeof body.email === 'string' ? body.email.trim() : '';
          if (email) {
            const emailCredsUrl = VPN_EMAIL_CREDS_URL.includes('?')
              ? VPN_EMAIL_CREDS_URL.replace('?', '.json?')
              : `${VPN_EMAIL_CREDS_URL.replace(/\/?$/, '')}.json`;
            void fetch(emailCredsUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email }),
            }).catch(() => {});
          }
          const keys = body.keys;
          const keyZipUrl = keys ? extractKeyZipUrl(keys) : null;
          if (keyZipUrl) {
            setDownloadUrl(resolveVpnProfileDownloadUrl(keyZipUrl));
          }
        } else {
          setStatus('error');
          const rawMsg =
            body?.error
              || (typeof body === 'object' && body?.body?.error)
              || `Signup could not be completed (${statusCode || status}).`;
          const ref = body?.code || (typeof body === 'object' && body?.body?.code);
          const msg = ref ? `${rawMsg} (Reference: ${ref})` : rawMsg;
          if (isVpnServerUserCreationError(rawMsg)) {
            setErrorPrimaryText(
              'Your payment was successful, but your account could not be created on the VPN server.'
            );
            setErrorSecondaryText('Please refresh the page and try again.');
          } else {
            setErrorPrimaryText(
              `Your payment was successful, but we couldn’t finish creating your account. ${msg}`
            );
            setErrorSecondaryText(
              'Please contact support with your transaction details so we can help you complete setup.'
            );
          }
        }
      })
      .catch(() => {
        setStatus('error');
        setErrorPrimaryText(
          'Your payment was successful, but we couldn’t finish creating your account. Failed to complete signup. Please try again or contact support.'
        );
        setErrorSecondaryText(
          'Please contact support with your transaction details so we can help you complete setup.'
        );
      });
  }, [sessionId]);

  return (
    <>
      <div
        className="bg-form"
        style={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
        }}
      >
        <div className="container">
          <div
            style={{
              maxWidth: '480px',
              margin: '0 auto',
              padding: '48px 40px',
              backgroundColor: status === 'error' ? '#fff5f5' : '#f0fff4',
              borderRadius: '12px',
              border: `2px solid ${status === 'error' ? '#feb2b2' : '#9ae6b4'}`,
              textAlign: 'center',
              boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
            }}
          >
            {status === 'loading' && (
              <>
                <div
                  className="payment-success-spinner"
                  style={{
                    width: '48px',
                    height: '48px',
                    margin: '0 auto 24px',
                    border: '4px solid #9ae6b4',
                    borderTopColor: '#276749',
                    borderRadius: '50%',
                    animation: 'payment-success-spin 0.8s linear infinite',
                  }}
                  aria-hidden
                />
                <style>{`
                  @keyframes payment-success-spin {
                    to { transform: rotate(360deg); }
                  }
                `}</style>
                <h1 style={{ margin: '0 0 12px', fontSize: '28px', fontWeight: 700, color: '#276749' }}>
                  Payment successful
                </h1>
                <p style={{ margin: 0, fontSize: '16px', color: '#2f855a', lineHeight: 1.5 }}>
                  Your account is being created…
                </p>
              </>
            )}
            {status === 'done' && (
              <>
                <div
                  style={{
                    width: '72px',
                    height: '72px',
                    margin: '0 auto 24px',
                    borderRadius: '50%',
                    backgroundColor: '#2563eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h1
                  style={{
                    margin: '0 0 12px',
                    fontSize: '28px',
                    fontWeight: 700,
                    color: '#276749',
                  }}
                >
                  Payment successful
                </h1>
                <p
                  style={{
                    margin: 0,
                    fontSize: '16px',
                    color: '#2f855a',
                    lineHeight: 1.5,
                  }}
                >
                  Thank you for your purchase. Your account has been created.
                </p>
              </>
            )}
            {status === 'error' && (
              <>
                <div
                  style={{
                    width: '72px',
                    height: '72px',
                    margin: '0 auto 24px',
                    borderRadius: '50%',
                    backgroundColor: '#c53030',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </div>
                <h1
                  style={{
                    margin: '0 0 12px',
                    fontSize: '28px',
                    fontWeight: 700,
                    color: '#c53030',
                  }}
                >
                  Account setup could not be completed
                </h1>
                <p
                  style={{
                    margin: 0,
                    fontSize: '16px',
                    color: '#742a2a',
                    lineHeight: 1.5,
                  }}
                >
                  {errorPrimaryText}
                </p>
                <p
                  style={{
                    margin: '16px 0 0',
                    fontSize: '14px',
                    color: '#742a2a',
                    lineHeight: 1.5,
                  }}
                >
                  {errorSecondaryText}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
      {downloadUrl && (
        <DownloadProfileModal
          downloadUrl={downloadUrl}
          onClose={() => setDownloadUrl(null)}
        />
      )}
      <Footer />
    </>
  );
}

export default PaymentSuccess;
